import EventEmitter from './EventEmitter'

export default class extends EventEmitter {
    /**
     * Constructor
     */
    constructor(options = {}) {
        super()

        // Set up
        this.time = +new Date()
        this.active = options.active || true
        this.$target = options.$target || window
        this.x = 0
        this.y = 0
        this.fromCenter = { x: 0, y: 0 }
        this.speed = { x: 0, y: 0 }
        this.dragOrigin = { x: 0, y: 0 }
        this.drag = { x: 0, y: 0 }
        this.dragDown = false
        this.fromDragOrigin = { x: 0, y: 0 }
        this.width = 0
        this.height = 0

        // Functions biding
        this.mouseMoveHandler = this.mouseMoveHandler.bind(this)
        this.mouseDownHandler = this.mouseDownHandler.bind(this)
        this.mouseUpHandler = this.mouseUpHandler.bind(this)
        this.touchMoveHandler = this.touchMoveHandler.bind(this)
        this.touchStartHandler = this.touchStartHandler.bind(this)
        this.touchEndHandler = this.touchEndHandler.bind(this)

        // Init
        if (this.active) {
            this.setListeners()
        }
    }

    /**
     * Set listeners (mouse, touch)
     */
    setListeners() {
        /**
         * Mouse
         */
        this.$target.addEventListener('mousedown', this.mouseDownHandler)
        window.addEventListener('mousemove', this.mouseMoveHandler)
        window.addEventListener('mouseup', this.mouseUpHandler)

        /**
         * Touch
         */
        this.$target.addEventListener('touchstart', this.touchStartHandler)
        window.addEventListener('touchmove', this.touchMoveHandler)
        window.addEventListener('touchend', this.touchEndHandler)
    }

    /**
     * Stop listening
     */
    unsetListeners() {
        /**
         * Mouse
         */
        this.$target.removeEventListener('mousedown', this.mouseDownHandler)
        window.removeEventListener('mousemove', this.mouseMoveHandler)
        window.removeEventListener('mouseup', this.mouseUpHandler)

        /**
         * Touch
         */
        this.$target.removeEventListener('touchstart', this.touchStartHandler)
        window.removeEventListener('touchmove', this.touchMoveHandler)
        window.removeEventListener('touchend', this.touchEndHandler)
    }

    /**
     * Get path for target
     * Fall back to event.path
     */
    getPathForTarget(target) {
        const path = []
        let currentElem = target

        while (currentElem) {
            path.push(currentElem)
            currentElem = currentElem.parentElement
        }

        if (path.indexOf(window) === -1 && path.indexOf(document) === -1) {
            path.push(document)
        }
        
        if (path.indexOf(window) === -1) {
            path.push(window)
        }

        return path
    }

    /**
     * Event handlers
     */
    mouseDownHandler(event) {
        let shouldOpen = true
        const preventTags = ['a', 'input', 'button']

        const path = event.path || this.getPathForTarget(event.target)
        
        // Test if clicked inside a functionnal tag
        for (let i = 0, length = path.length; i < length; i++) {
            const $element = path[i]
            const tagName = $element.tagName ? $element.tagName.toLowerCase() : ''

            if (preventTags.indexOf(tagName) !== -1) {
                shouldOpen = false
            }
        }

        // Should open
        if (shouldOpen) {
            event.preventDefault()
            this.setDrag(event.clientX, event.clientY)
        }
    }

    mouseMoveHandler(event) {
        event.preventDefault()

        this.setPosition(event.clientX, event.clientY)
    }

    mouseUpHandler(event) {
        event.preventDefault()

        this.unsetDrag(event.clientX, event.clientY)
    }

    touchStartHandler(event) {
        let shouldOpen = true
        const preventTags = ['a', 'input', 'button']

        const path = event.path || this.getPathForTarget(event.target)
        
        // Test if clicked inside a functionnal tag
        for (let i = 0, length = path.length; i < length; i++) {
            const $element = path[i]
            const tagName = $element.tagName ? $element.tagName.toLowerCase() : ''

            if (preventTags.indexOf(tagName) !== -1) {
                shouldOpen = false
            }
        }

        // Should open
        if (shouldOpen) {
            this.setPosition(event.changedTouches[0].screenX, event.changedTouches[0].screenY)
            this.setDrag(event.changedTouches[0].screenX, event.changedTouches[0].screenY)
        }
    }

    touchMoveHandler(event) {
        this.setPosition(event.changedTouches[0].screenX, event.changedTouches[0].screenY)
    }

    touchEndHandler(event) {
        this.unsetDrag(event.changedTouches[0].screenX, event.changedTouches[0].screenY)
    }

    /**
     * Activate
     */
    activate() {
        // Already active
        if (this.active) {
            return
        }

        this.active = true

        this.setListeners()
    }

    /**
     * Deactivate
     */
    deactivate() {
        // Already inactive
        if (!this.active) {
            return
        }

        this.active = false

        this.unsetListeners()
    }

    /**
     * Set position
     * From any source (mouse, touch)
     * Can be triggered from outside
     */
    setPosition(x, y) {
        // No change
        if (this.x === x && this.y === y) {
            return
        }

        // Time
        const time = +new Date()
        const delta = time - this.time
        this.time = time

        // Update values
        const newX = x / this.width
        const newY = y / this.height

        this.speed.x = (newX - this.x) / delta
        this.speed.y = (newY - this.y) / delta

        this.x = newX
        this.y = newY

        this.fromCenter.x = this.x - 0.5
        this.fromCenter.y = this.y - 0.5

        // Trigger event
        this.trigger('position-update')

        // Is dragging
        if (this.dragDown) {
            // Update values
            this.fromDragOrigin.x = this.x - this.dragOrigin.x
            this.fromDragOrigin.y = this.y - this.dragOrigin.y

            // Trigger event
            this.trigger('drag-update')
        }
    }

    /**
     * Set drag
     */
    setDrag() {
        // Update values
        this.dragDown = true

        this.dragOrigin.x = this.x
        this.dragOrigin.y = this.y

        this.drag.x = 0
        this.drag.y = 0

        // Trigger event
        this.trigger('drag-start')
    }

    /**
     * Unset drag
     */
    unsetDrag() {
        // Update values
        this.dragDown = false

        // Trigger event
        this.trigger('drag-stop')

        // Reset values
        this.drag.x = 0
        this.drag.y = 0

        this.fromDragOrigin.x = 0
        this.fromDragOrigin.y = 0
    }

    /**
     * Resize
     */
    resize(width, height) {
        this.width = width
        this.height = height
    }

    /**
     * Dispose
     */
    dispose() {
        this.unsetListeners()
    }
}


// WEBPACK FOOTER //
// ./src/scripts/libs/experience/Cursor.js